import { useEffect } from "react";

/**
 * Renders the assistant's markdown. Every surface this mounts on is canvas — chat
 * bubbles, the output panel, the blueprint panel — so it reads canvas tokens and
 * follows the panel-mode toggle.
 *
 * Headings take `color: inherit` rather than a token of their own. They are
 * already distinguished by weight and case, and inheriting is what lets the same
 * markdown render correctly inside a user bubble, where the text colour is
 * `--you-text` on the indigo user bubble rather than `--text-primary` on the canvas.
 */
/* ONE LOADER for a Google font, shared by the chat's previews and Me → Brand:
   a stylesheet link, added once per font. A name Google doesn't have simply
   doesn't load, and the text shows in the fallback. */
export function loadGoogleFont(name) {
  const n = String(name || "").trim();
  if (!n || typeof document === "undefined" || document.querySelector(`link[data-gfont="${n}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${n.replace(/ /g, "+")}:wght@400;700&display=swap`;
  link.setAttribute("data-gfont", n);
  document.head.appendChild(link);
}

export function FontPreview({ name }) {
  useEffect(() => { loadGoogleFont(name); }, [name]);
  return (
    <span style={{
      display: "inline-flex", flexDirection: "column", gap: 3,
      background: "var(--bg-primary)", border: "1px solid var(--accent-line)",
      borderRadius: 7, padding: "7px 12px", margin: "3px 0",
      verticalAlign: "middle",
    }}>
      <span style={{ fontFamily: `"${name}", sans-serif`, fontSize: 18, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.2 }}>{name}</span>
      <span style={{ fontFamily: `"${name}", sans-serif`, fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>Aa Bb Cc — The quick brown fox jumps over the lazy dog</span>
    </span>
  );
}

/**
 * Link targets in rendered markdown come out of model output, which is
 * untrusted input however friendly its source. React 19 already rewrites
 * `javascript:` hrefs into a thrown error, but that is a backstop in one
 * library version, not a policy — so allow only the schemes a link in
 * generated copy should ever use, and render anything else as plain text.
 *
 * A bare domain gets https:// rather than being left as a relative path,
 * which is what `[Brill](tevrawood.com)` would otherwise resolve to.
 */
const ALLOWED_SCHEME = /^(?:https?|mailto|tel):/i;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

export function safeHref(url) {
  const u = (url || "").trim();
  if (!u) return null;
  if (u.startsWith("/") || u.startsWith("#")) return u;
  if (HAS_SCHEME.test(u)) return ALLOWED_SCHEME.test(u) ? u : null;
  return `https://${u}`;
}

export function renderInline(text, keyPrefix) {
  return text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\)|#[A-Fa-f0-9]{6}\b|\{\{[^}]+\}\})/g).map((part, i) => {
    const k = `${keyPrefix}-${i}`;
    if (part.startsWith("**") && part.endsWith("**"))
      return <strong key={k}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*"))
      return <em key={k}>{part.slice(1, -1)}</em>;
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const href = safeHref(linkMatch[2]);
      // A rejected scheme still shows its text — dropping the label would lose
      // meaning from the copy, and a dead link is the safe failure here.
      if (!href) return <span key={k}>{linkMatch[1]}</span>;
      return <a key={k} href={href} target="_blank" rel="noreferrer" style={{ color: "var(--accent)", textDecoration: "underline" }}>{linkMatch[1]}</a>;
    }
    if (/^#[A-Fa-f0-9]{6}$/i.test(part))
      return (
        <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5, verticalAlign: "middle" }}>
          <span style={{ display: "inline-block", width: 13, height: 13, borderRadius: 3, background: part, border: "1px solid var(--border-strong)", flexShrink: 0 }} />
          <span style={{ fontFamily: "monospace", fontSize: "0.88em" }}>{part}</span>
        </span>
      );
    if (part.startsWith("{{") && part.endsWith("}}"))
      return <FontPreview key={k} name={part.slice(2, -2)} />;
    return <span key={k}>{part}</span>;
  });
}

export function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (trimmed === "") { i++; continue; }
    if (/^-{3,}$/.test(trimmed)) {
      out.push(<hr key={`hr-${i}`} style={{ border: "none", borderTop: "1px solid var(--border-strong)", margin: "16px 0" }} />);
      i++; continue;
    }
    if (trimmed.startsWith("### ")) {
      out.push(<p key={`h3-${i}`} style={{ fontWeight: 700, fontSize: 11, color: "inherit", margin: "18px 0 4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{renderInline(trimmed.slice(4), `h3-${i}`)}</p>);
      i++; continue;
    }
    if (trimmed.startsWith("## ")) {
      out.push(<p key={`h2-${i}`} style={{ fontWeight: 700, color: "inherit", margin: "20px 0 5px" }}>{renderInline(trimmed.slice(3), `h2-${i}`)}</p>);
      i++; continue;
    }
    if (trimmed.startsWith("# ")) {
      out.push(<p key={`h1-${i}`} style={{ fontWeight: 700, color: "inherit", margin: "22px 0 7px", fontSize: "1.05em" }}>{renderInline(trimmed.slice(2), `h1-${i}`)}</p>);
      i++; continue;
    }
    if (/^[-*] /.test(line)) {
      const startI = i;
      const items = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(<li key={i} style={{ marginBottom: 4 }}>{renderInline(lines[i].slice(2).trim(), `li-${i}`)}</li>);
        i++;
      }
      out.push(<ul key={`ul-${startI}`} style={{ paddingLeft: 18, margin: "6px 0 12px", listStyleType: "disc" }}>{items}</ul>);
      continue;
    }
    /* ── Numbered lists that wrap ────────────────────────────────────────
     *
     * THE BUG (2026-08-30, screenshotted from a live chat). This loop only
     * continued while the NEXT line also began `1. `, so the first WRAPPED line
     * of an item closed the list. Brill writes items that run onto indented
     * continuation lines, so a three-point answer became three separate
     * one-item lists — and because every `<ol>` restarts at one, all three
     * rendered as "1.".
     *
     * Not a regression: this code is unchanged since a59d19a. What changed is
     * the shape of what Brill writes.
     *
     * TWO FIXES, deliberately, because either alone leaves a way to be wrong:
     *   - continuation lines are folded into the item they belong to, so a
     *     wrapped list stays ONE list;
     *   - `start` carries the item's real number, so if anything else ever
     *     breaks a list in two, the second half still counts from where the
     *     first left off instead of silently restarting at one.
     *
     * A nested bullet still ends the list. That is the pre-existing behaviour
     * and it renders acceptably; swallowing it into the prose of the item above
     * would be worse than a break. */
    if (/^\s*\d+\. /.test(line)) {
      const startI = i;
      const items = [];
      const firstNum = parseInt(line.trim().match(/^(\d+)\./)[1], 10);
      while (i < lines.length && /^\s*\d+\. /.test(lines[i])) {
        const parts = [lines[i].replace(/^\s*\d+\.\s+/, "")];
        const itemI = i;
        i++;
        /* A continuation is INDENTED, non-empty, and not the start of anything
           else. The indent is what distinguishes "the rest of this item" from
           "a new paragraph after the list" — an unindented line ends it. */
        while (
          i < lines.length &&
          lines[i].trim() !== "" &&
          /^\s+/.test(lines[i]) &&
          !/^\s*\d+\. /.test(lines[i]) &&
          !/^\s*[-*] /.test(lines[i])
        ) { parts.push(lines[i].trim()); i++; }
        items.push(
          <li key={itemI} style={{ marginBottom: 4 }}>
            {renderInline(parts.join(" "), `oli-${itemI}`)}
          </li>
        );
      }
      out.push(<ol key={`ol-${startI}`} start={firstNum} style={{ paddingLeft: 18, margin: "6px 0 12px" }}>{items}</ol>);
      continue;
    }
    const startI = i;
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^#{1,3} /.test(lines[i].trim()) &&
      !/^[-*] /.test(lines[i]) &&
      /* Matches the ordered-list opener above, indent included — otherwise an
         indented `1. ` would be swallowed into a paragraph and never render as
         a list at all. */
      !/^\s*\d+\. /.test(lines[i]) &&
      !/^-{3,}$/.test(lines[i].trim())
    ) { paraLines.push(lines[i]); i++; }
    if (paraLines.length) {
      const content = [];
      paraLines.forEach((l, li) => {
        renderInline(l, `p-${startI}-${li}`).forEach(el => content.push(el));
        if (li < paraLines.length - 1) content.push(<br key={`br-${startI}-${li}`} />);
      });
      out.push(<p key={`p-${startI}`} style={{ margin: "0 0 12px" }}>{content}</p>);
    }
  }
  return out.length ? out : <span>{text}</span>;
}
